import { ApolloError } from '@apollo/client';
import { Trans } from 'react-i18next';
import { useContext, useEffect, useRef, useState } from 'react';

import { getKeyedSellables, getTicketsForSale } from '../helpers';
import { getServerErrors, guessCountry, guessLocale, guessTimezone, isInViewport } from '../../../common/helpers';
import { useEvent } from '../../Common/EventProvider';
import { useTracking } from '../../../common/Tracking';
import CheckoutContext from '../CheckoutContext';
import RegistrationForm from './RegistrationForm';
import StickyHeader from '../../Common/StickyHeader';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

/**
 * Shows the form for the currently active registration.
 */
const PersonalisationForm = () => {
  const { track } = useTracking();
  const { event } = useEvent();

  const {
    personalisation,
    setActiveRegistration,
    error,
  } = useContext(CheckoutContext);

  const registrations = personalisation.personalisableRegistrations;
  const registration = personalisation.activeRegistration;
  const total = registrations.length;

  // If there is no active registration anymore (because activeIndex could be outdated), reset it.
  useEffect(() => {
    if (!registration) {
      setActiveRegistration(personalisation.firstIndex);
    }
  }, [registration, personalisation.firstIndex, setActiveRegistration]);

  const errorRef = useRef<ApolloError>();

  // If there is a server error, activate the first registration form with an error.
  useEffect(() => {
    if (error !== errorRef.current) {
      const errors = getServerErrors(error);

      // These point to indices in the form state
      const serverIndices = error
        // Extract the registration index from the error key
        ? Object.keys(errors).map((key) => {
          const match = key.match(/^registrations\.create\.([0-9]*)\./);
          return match?.length > 1 ? parseInt(match[1], 10) : -1;
        }).filter((index) => index > -1)
        : [];

      // Map them to indices of personalisable registrations
      const errorIndices = serverIndices.map((index) => (
        personalisation.personalisableFormIndices.findIndex((i) => i === index)
      ));

      if (errorIndices.length > 0) {
        setActiveRegistration(Math.min(...errorIndices));
      }
    }

    errorRef.current = error;
  }, [error, setActiveRegistration, personalisation.personalisableFormIndices]);

  useEffect(() => {
    track?.('Checkout:RegistrationOpened', { index: personalisation.activeFormIndex, total });
  }, [track, total, personalisation.activeFormIndex]);

  const countryRef = useRef(registration?.details?.country);

  useEffect(() => {
    // Collect statistics on country change events, to monitor whether the prefilled countries are accurate.
    if (registration?.details?.country !== countryRef.current) {
      track?.('Checkout:RegistrationCountryChanged', {
        index: personalisation.activeFormIndex,
        new_country: registration?.details?.country,
        previous_country: guessCountry(event.project),
        timezone: guessTimezone(),
        locale: guessLocale(),
      });

      countryRef.current = registration?.details?.country;
    }
  }, [track, registration?.details?.country, event.project, personalisation.activeFormIndex]);

  if (!registration) {
    return null;
  }

  return (
    <>
      <Header />
      <RegistrationForm key={personalisation.activeFormIndex} />
    </>
  );
};

const Header = () => {
  const { formatNumber } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();
  const { event } = useEvent();

  const { personalisation } = useContext(CheckoutContext);

  const registration = personalisation.activeRegistration;

  const total = personalisation.skips.length;

  const currentProgress = ((personalisation.activeIndex + 1) / total) * 100;

  const progressBarRef = useRef<HTMLDivElement | null>(null);

  // Trigger animation on mount only when the active index is the first index.
  const [progress, setProgress] = useState(
    personalisation.activeIndex === personalisation.firstIndex ? 0 : currentProgress,
  );

  useEffect(() => {
    // If the progress bar is not in the viewport, add a small delay,
    // so that a potential scrollTop() may be completed first.
    const delay = progressBarRef.current && isInViewport(progressBarRef.current) ? 0 : 250;

    const timer = window.setTimeout(() => {
      setProgress(currentProgress);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [currentProgress]);

  const tickets = getTicketsForSale(event);
  const ticket = getKeyedSellables(tickets)[registration.purchase.promotion.id];

  const promotion = ticket.promotions_for_sale.filter(
    (promotion) => promotion.id === registration.purchase.promotion.id,
  )[0];

  const timeSlot = ticket.upcoming_time_slots.filter(
    (timeSlot) => timeSlot.id === registration.time_slot?.id,
  )[0];

  return (
    <StickyHeader background="gray.50" key={personalisation.activeIndex}>
      {(isSticky) => (
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          <UI.FadeIn sc={{ py: 0.25 }}>
            <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
              <UI.H4 sc={{ strong: true, small: isSticky, mr: 2 }}>
                <UI.Icon>
                  <UI.Icons.Ticket />
                </UI.Icon>
                {' '}
                {ticket.title}
              </UI.H4>
              {isSticky && (
                <UI.Small>
                  <UI.Badge
                    sc={{ brand: 'secondary', my: -0.5 }}
                    style={{
                      minWidth: 'auto',
                      padding: '0 .75em',
                      lineHeight: '2em',
                      height: '2em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Trans
                      i18nKey="personalisation_form.ticket_x_of_y"
                      values={{ x: formatNumber(personalisation.activeIndex + 1), y: formatNumber(total) }}
                    >
                      <UI.Strong />
                      <UI.Strong />
                    </Trans>
                  </UI.Badge>
                </UI.Small>
              )}
            </UI.FlexContainer>
            {!isSticky && (promotion.title || timeSlot) && (
              <UI.Div sc={{ mt: 1 }}>
                <UI.Delimit>
                  {timeSlot && formatTimeSlot(timeSlot)}
                  {timeSlot?.title}
                  {promotion.title}
                </UI.Delimit>
              </UI.Div>
            )}
          </UI.FadeIn>
          {!isSticky && (
            <UI.Div>
              <UI.Div
                sc={{ background: 'gray.200', mb: 1 }}
                style={{ height: 4, borderRadius: 4 }}
                ref={progressBarRef}
              >
                <UI.Div
                  sc={{ background: 'secondary' }}
                  style={{
                    height: 4,
                    width: `${progress}%`,
                    transition: 'all 0.5s ease-in-out',
                    borderRadius: 4,
                  }}
                />
              </UI.Div>
              <UI.Span
                style={{ opacity: progress > 0 ? 1 : 0.3, transition: 'all 0.15s ease-in-out' }}
              >
                <Trans
                  i18nKey="personalisation_form.personalising_ticket_x_of_y"
                  values={{ count: formatNumber(personalisation.activeIndex + 1), total: formatNumber(total) }}
                >
                  <UI.Strong />
                  <UI.Strong />
                </Trans>
              </UI.Span>
            </UI.Div>
          )}
        </UI.GridContainer>
      )}
    </StickyHeader>
  );
};

export default PersonalisationForm;
